import { useState, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Typography, Alert } from 'antd'
import {
  ArrowLeftOutlined,
  MailOutlined,
  RobotOutlined,
  HistoryOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import { useQueryClient, useQueries } from '@tanstack/react-query'
import {
  usePracticeAgentWorkspaceInit,
  usePracticeAgentWorkspaceGroupInsight,
  useWorkspaceDimensions,
} from '../hooks'
import { fetchWorkspaceInsight, fetchWorkspaceGroupInsight } from '../api'
import { queryKeys } from '../queryKeys'
import { useToast } from '../../../contexts/ToastContext'
import { useAuthSession } from '../../../lib/session'
import type { PracticeAgentReportData } from '../types'
import PageLoading from '../../../components/PageLoading'
import FeatureThemeProvider from '../../../components/FeatureThemeProvider'
import { generateWorkspacePdfBlob } from './pdfExport'
import { getFeatureThemeClassName, getFeatureThemeStyle, productThemes } from '../../../lib/productBranding'
import {
  ReportPanel,
  CommunicateModal,
  ChatPanel,
  NotePanel,
  InsightCard,
} from './components'

const { Title } = Typography

export default function WorkspacePage() {
  const session = useAuthSession()
  const clientId = session?.currentClientId ?? ''
  const queryClient = useQueryClient()
  const { reportBuilderId, reportBuilderGroupId } = useParams<{
    reportBuilderId?: string
    reportBuilderGroupId?: string
  }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { addToast } = useToast()
  const featureThemeStyle = getFeatureThemeStyle(productThemes.pracIntel)
  const exportRootRef = useRef<HTMLDivElement | null>(null)
  const groupInsightExportRef = useRef<HTMLDivElement | null>(null)

  const reopenVersionId = searchParams.get('deepDiveVersion') ?? undefined
  const reopenDrilldownFilters = searchParams.get('drilldownFilters')
  const parsedDrilldownFilters = reopenDrilldownFilters
    ? (() => { try { return JSON.parse(reopenDrilldownFilters) as Record<string, string> } catch { return null } })()
    : null
  const drilldownDimensionParam = searchParams.get('drilldownDimension') ?? undefined
  const initialDrilldown =
    parsedDrilldownFilters && Object.keys(parsedDrilldownFilters).length > 0
      ? { filters: parsedDrilldownFilters, versionId: reopenVersionId, dimension: drilldownDimensionParam }
      : null
  const drilldownReportBuilderId = searchParams.get('drilldownReportBuilderId') ?? undefined

  const isGroup = !!reportBuilderGroupId

  const { data: workspaceDimensions } = useWorkspaceDimensions(
    clientId,
    isGroup ? undefined : reportBuilderId,
    isGroup ? reportBuilderGroupId : undefined,
  )

  const [communicateOpen, setCommunicateOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  const handleNoteCreated = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.notes(clientId, reportBuilderId, reportBuilderGroupId),
    })
  }, [queryClient, clientId, reportBuilderId, reportBuilderGroupId])

  const { data: groupWorkspaceInit, isLoading: groupLoading, isError: groupError } = usePracticeAgentWorkspaceInit(
    clientId,
    undefined,
    isGroup ? reportBuilderGroupId : undefined,
  )
  const groupData = groupWorkspaceInit?.groupData

  const { data: singleWorkspaceInit } = usePracticeAgentWorkspaceInit(
    clientId,
    isGroup ? undefined : reportBuilderId,
    undefined,
  )
  const singleReportTitle = singleWorkspaceInit?.reportData?.title

  // Fire per-report insight calls in parallel. React Query shares the cache with each
  // ReportPanel's own usePracticeAgentWorkspaceInsight call, so no duplicate requests.
  const groupReportIds = groupData?.reports.map((r: PracticeAgentReportData) => r.reportBuilderId) ?? []
  const reportInsightQueries = useQueries({
    queries: groupReportIds.map((rbId: string) => ({
      queryKey: queryKeys.workspaceInsight(clientId, rbId, {}),
      queryFn: () => fetchWorkspaceInsight({ clientId, reportBuilderId: rbId, filters: {} }),
      enabled: isGroup && !!clientId && !!rbId,
      staleTime: 5 * 60 * 1000,
      gcTime: 60 * 1000,
    })),
  })
  const allReportInsightsDone =
    !!groupData && groupReportIds.length > 0 && reportInsightQueries.every((q) => !q.isPending)

  const { data: groupInsightsData, isLoading: groupInsightsLoading } = usePracticeAgentWorkspaceGroupInsight(
    clientId,
    isGroup ? reportBuilderGroupId : undefined,
    {},
    { enabled: allReportInsightsDone },
  )
  const groupInsight = groupInsightsData?.groupInsight
  const groupInsightLoading = groupInsightsLoading

  const handleOpenHistory = useCallback(() => {
    const params = new URLSearchParams()
    if (reportBuilderGroupId) {
      params.set('reportBuilderGroupId', reportBuilderGroupId)
    } else if (reportBuilderId) {
      params.set('reportBuilderId', reportBuilderId)
    }

    const query = params.toString()
    navigate(`/reports/workspace/history${query ? `?${query}` : ''}`)
  }, [navigate, reportBuilderGroupId, reportBuilderId])

  const getPdfFilename = useCallback(() => {
    const date = new Date().toISOString().slice(0, 10)
    const raw = isGroup
      ? (groupData?.groupName ?? 'Group')
      : (singleReportTitle ?? 'Report')
    const safe = raw.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-')
    return `${safe}-${date}.pdf`
  }, [isGroup, groupData?.groupName, singleReportTitle])

  const handleDownloadPdf = useCallback(async () => {
    try {
      setIsDownloadingPdf(true)

      // For group workspaces: ensure the AI Group Insight has finished generating
      // before capturing its DOM node. queryClient.fetchQuery returns the cached
      // result instantly if available, or awaits the in-flight/new request otherwise.
      if (isGroup && reportBuilderGroupId) {
        await queryClient.fetchQuery({
          queryKey: queryKeys.workspaceGroupInsight(clientId, reportBuilderGroupId, {}),
          queryFn: () => fetchWorkspaceGroupInsight({ clientId, reportBuilderGroupId, filters: {} }),
          staleTime: 5 * 60 * 1000,
        })
      }

      const pdfBlob = await generateWorkspacePdfBlob(exportRootRef, groupInsightExportRef, clientId, reportBuilderGroupId)
      if (!pdfBlob) {
        addToast('Nothing to export in this workspace.', 'warning')
        return
      }
      const url = URL.createObjectURL(pdfBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = getPdfFilename()
      a.click()
      URL.revokeObjectURL(url)
      addToast('Workspace PDF downloaded.', 'success')
    } catch {
      addToast('Failed to generate workspace PDF.', 'error')
    } finally {
      setIsDownloadingPdf(false)
    }
  }, [addToast, isGroup, reportBuilderGroupId, clientId, queryClient, getPdfFilename])

  // ── Group view ──────────────────────────────────────────────────────────
  if (isGroup) {
    return (
      <FeatureThemeProvider featureKey="pracIntel" withAntd>
      <div className={`${getFeatureThemeClassName('pracIntel')} space-y-6`} ref={exportRootRef} style={featureThemeStyle}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports/created')} type="text" aria-label="Back" />
            <div>
              <Title level={4} className="!mb-0">
                {groupData?.groupName ?? 'Group'}
              </Title>
              <p className="text-sm text-gray-500">
                PracIntel group workspace
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button icon={<RobotOutlined />} onClick={() => setChatOpen(true)}>
              AI Chat
            </Button>
            <Button icon={<HistoryOutlined />} onClick={handleOpenHistory}>
              History
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => setNoteOpen(true)}>
              Note
            </Button>
            <Button icon={<MailOutlined />} onClick={() => setCommunicateOpen(true)}>
              Communicate
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadPdf} loading={isDownloadingPdf}>
              Download
            </Button>
          </div>
        </div>

        <div ref={groupInsightExportRef} data-export-group-insight="true" data-export-title="Group Insight">
          <InsightCard
            insight={groupInsight?.insight}
            isLoading={groupInsightLoading}
            label="PracIntel Group Insight"
          />
        </div>

        <PageLoading show={groupLoading} theme={productThemes.pracIntel} />
        {!groupLoading && (groupError ? (
          <Alert message="Failed to load group data" type="error" showIcon />
        ) : groupData ? (
          <>
          <div className="flex flex-col gap-6">
            {groupData.reports.map((r: PracticeAgentReportData) => (
              <ReportPanel
                key={r.reportBuilderId}
                clientId={clientId}
                reportBuilderId={r.reportBuilderId}
                reportBuilderGroupId={reportBuilderGroupId}
                initialDrilldown={drilldownReportBuilderId === r.reportBuilderId ? initialDrilldown : null}
                groupReportData={r}
                drilldownDimensions={workspaceDimensions?.dimensions}
                defaultDrilldownDimension={workspaceDimensions?.recentDimensions?.[r.reportBuilderId] ?? null}
              />
            ))}
          </div>
          </>
        ) : null)}

        <CommunicateModal
          open={communicateOpen}
          onClose={() => setCommunicateOpen(false)}
          clientId={clientId}
          reportBuilderGroupId={reportBuilderGroupId}
          generatePdfBlob={() => generateWorkspacePdfBlob(exportRootRef, groupInsightExportRef, clientId, reportBuilderGroupId)}
          pdfFilename={getPdfFilename()}
        />
        <ChatPanel
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          reportBuilderGroupId={reportBuilderGroupId}
          reportBuilderIds={groupData?.reports.map((r: PracticeAgentReportData) => r.reportBuilderId)}
          clientId={clientId}
          title={groupData?.groupName}
          onNoteCreated={handleNoteCreated}
        />
        <NotePanel
          open={noteOpen}
          onClose={() => setNoteOpen(false)}
          clientId={clientId}
          reportBuilderId={undefined}
          reportBuilderGroupId={reportBuilderGroupId}
          title={groupData?.groupName}
        />
      </div>
      </FeatureThemeProvider>
    )
  }

  // ── Single report view ──────────────────────────────────────────────────
  return (
    <FeatureThemeProvider featureKey="pracIntel" withAntd>
    <div className={`${getFeatureThemeClassName('pracIntel')} w-full mx-auto space-y-6`} ref={exportRootRef} style={featureThemeStyle}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports/created')} type="text" aria-label="Back" />
          <div>
            <Title level={4} className="!mb-0">PracIntel Workspace</Title>
            <p className="text-sm text-gray-500">Click any data point to drill down.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button icon={<RobotOutlined />} onClick={() => setChatOpen(true)}>
            Chat
          </Button>
          <Button icon={<HistoryOutlined />} onClick={handleOpenHistory}>
            History
          </Button>
          <Button icon={<FileTextOutlined />} onClick={() => setNoteOpen(true)}>
            Note
          </Button>
          <Button icon={<MailOutlined />} onClick={() => setCommunicateOpen(true)}>
            Communicate
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadPdf} loading={isDownloadingPdf}>
            Download
          </Button>
        </div>
      </div>

      {reportBuilderId && (
        <ReportPanel
          clientId={clientId}
          reportBuilderId={reportBuilderId}
          initialDrilldown={initialDrilldown}
          drilldownDimensions={workspaceDimensions?.dimensions}
          defaultDrilldownDimension={workspaceDimensions?.recentDimensions?.[reportBuilderId] ?? null}
        />
      )}

      <CommunicateModal
        open={communicateOpen}
        onClose={() => setCommunicateOpen(false)}
        clientId={clientId}
        reportBuilderId={reportBuilderId}
        generatePdfBlob={() => generateWorkspacePdfBlob(exportRootRef, groupInsightExportRef, clientId, reportBuilderGroupId)}
        pdfFilename={getPdfFilename()}
      />
      <ChatPanel
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        reportBuilderId={reportBuilderId}
        clientId={clientId}
        onNoteCreated={handleNoteCreated}
      />
      <NotePanel
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        clientId={clientId}
        reportBuilderId={reportBuilderId}
      />
    </div>
    </FeatureThemeProvider>
  )
}
